import { z } from 'zod';

/**
 * Canonical error codes — docs/02_OPENAPI.yaml `ErrorResponse.code` and
 * docs/00_TECHNICAL_ARCHITECTURE_v1.2.md §19.
 *
 * `CONFIG_VERSION_MISMATCH` is part of the wire enum but is **reserved**: a
 * stale `event_config_version` is accepted, never rejected (AGENTS.md
 * invariant 6). Nothing in Phase 1 may return it, which is why it is absent
 * from `RETURNABLE_ERROR_CODES` and from `ERROR_STATUS`.
 */
export const ERROR_CODES = [
  'INVALID_REQUEST',
  'EVENT_NOT_FOUND',
  'EVENT_NOT_ACTIVE',
  'INVALID_LOCATION',
  'CHECKOUT_NOT_ENABLED',
  'SLUG_TAKEN',
  'CONFIG_VERSION_MISMATCH',
  'TURNSTILE_FAILED',
  'REQUEST_TOO_LARGE',
  'TEMPORARY_UNAVAILABLE',
  'NOT_FOUND',
] as const;

export const RESERVED_ERROR_CODES = ['CONFIG_VERSION_MISMATCH'] as const;

export const RETURNABLE_ERROR_CODES = ERROR_CODES.filter(
  (code): code is ReturnableErrorCode =>
    !(RESERVED_ERROR_CODES as readonly string[]).includes(code),
);

export type ErrorCode = (typeof ERROR_CODES)[number];
export type ReservedErrorCode = (typeof RESERVED_ERROR_CODES)[number];
export type ReturnableErrorCode = Exclude<ErrorCode, ReservedErrorCode>;

/** One HTTP status per code — the mapping FE and BE must both implement. */
export const ERROR_STATUS: Record<ReturnableErrorCode, number> = {
  INVALID_REQUEST: 400,
  CHECKOUT_NOT_ENABLED: 400,
  TURNSTILE_FAILED: 403,
  EVENT_NOT_FOUND: 404,
  NOT_FOUND: 404,
  EVENT_NOT_ACTIVE: 409,
  INVALID_LOCATION: 409,
  SLUG_TAKEN: 409,
  REQUEST_TOO_LARGE: 413,
  TEMPORARY_UNAVAILABLE: 503,
};

export const ErrorCodeSchema = z.enum(ERROR_CODES);

export const ErrorResponseSchema = z.strictObject({
  ok: z.literal(false),
  code: ErrorCodeSchema,
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
